/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { CheckboxProps } from '../../types';
import './Checkbox.less';

const CheckIcon = () => (
  <svg className="checkbox__icon checkbox__icon--check" viewBox="0 0 12 12" aria-hidden="true">
    <path
      d="M2 6.2 4.7 9 10 3.4"
      stroke="currentColor"
      strokeWidth="1.75"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IndeterminateIcon = () => (
  <svg className="checkbox__icon checkbox__icon--indeterminate" viewBox="0 0 12 12" aria-hidden="true">
    <line x1="3" y1="6" x2="9" y2="6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ indeterminate = false, className, checked, defaultChecked, disabled, onChange, ...rest }, ref) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      },
      [ref]
    );

    // Sync the DOM-only `indeterminate` property whenever it changes.
    React.useEffect(() => {
      if (innerRef.current) {
        innerRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    return (
      <span
        className={cx(
          'checkbox',
          {
            'checkbox--checked': !!checked,
            'checkbox--disabled': !!disabled,
            'checkbox--indeterminate': indeterminate,
          },
          className
        )}>
        <input
          ref={setRefs}
          type="checkbox"
          className="checkbox__input"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={onChange}
          {...rest}
        />
        <span className="checkbox__box" aria-hidden="true">
          {indeterminate ? <IndeterminateIcon /> : <CheckIcon />}
        </span>
      </span>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
